import {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
} from "react";

import {
  TestData,
  TestQuestionData,
  TestQuestionFrontType,
  ValidationError,
} from "../../types";

export type ContinueQuestion = Pick<
  TestData["questions"][number],
  "answer" | "answers" | "leftAnswers" | "rightAnswers" | "userAnswer"
>;

export type SaveAnswerPayload = {
  answerValue: string,
  fileAnswer?: string,
  userComment?: string,
};

export type SaveAnswerHandler = (payload: SaveAnswerPayload) => Promise<unknown>;

export type TestSolutionState = {
  questionType?: TestQuestionFrontType,
  questionCurrentIndex: number,
  userAnswer: unknown,
  fileAnswer: string,
  isFileUpload: boolean,
  answers: string[],
  currentQuestion: TestQuestionData,
  isCommentRequiredAndEmpty: boolean,
  userComment: string,
  error: ValidationError,
  isSaveAnswerLoading: boolean,
  checkComponentCover: boolean,
  isTestInProgress: boolean,
  isTestExpired: boolean,
  isTestFinished: boolean,
  testDataReceivedAt?: number,
  remainingTime: number | null,
  hasTestTimeout: boolean,
};

export const enum TestSolutionActionType {
  QuestionLoadedFromServer = "questionLoadedFromServer",
  AnswerChanged = "answerChanged",
  FileChanged = "fileChanged",
  CommentChanged = "commentChanged",
  ValidationChanged = "validationChanged",
  CommentRequiredChanged = "commentRequiredChanged",
  SaveLoadingChanged = "saveLoadingChanged",
  PrevQuestion = "prevQuestion",
  NextQuestionApplied = "nextQuestionApplied",
  ContinueTest = "continueTest",
  StartTest = "startTest",
  RestartTest = "restartTest",
  SetQuestionIndex = "setQuestionIndex",
  ToggleCheckCover = "toggleCheckCover",
  FinishTestSucceeded = "finishTestSucceeded",
  FinishTestExpired = "finishTestExpired",
  TimerSynced = "timerSynced",
  TimerElapsed = "timerElapsed",
  ResumeAfterTimeout = "resumeAfterTimeout",
}

export type TestSolutionAction =
  | {
    type: TestSolutionActionType.QuestionLoadedFromServer,
    payload?: TestQuestionData,
  } |
  {
    type: TestSolutionActionType.AnswerChanged,
    payload: SetStateAction<unknown>,
  } |
  {
    type: TestSolutionActionType.FileChanged,
    payload: SetStateAction<string>,
  } |
  {
    type: TestSolutionActionType.CommentChanged,
    payload: SetStateAction<string>,
  } |
  {
    type: TestSolutionActionType.ValidationChanged,
    payload: SetStateAction<ValidationError>,
  } |
  {
    type: TestSolutionActionType.CommentRequiredChanged,
    payload: boolean,
  } |
  {
    type: TestSolutionActionType.SaveLoadingChanged,
    payload: boolean,
  } |
  {
    type: TestSolutionActionType.PrevQuestion,
  } |
  {
    type: TestSolutionActionType.NextQuestionApplied,
    payload: {
      questionCount: number,
    },
  } |
  {
    type: TestSolutionActionType.ContinueTest,
    payload: ContinueQuestion[],
  } |
  {
    type: TestSolutionActionType.StartTest,
  } |
  {
    type: TestSolutionActionType.RestartTest,
  } |
  {
    type: TestSolutionActionType.SetQuestionIndex,
    payload: number,
  } |
  {
    type: TestSolutionActionType.ToggleCheckCover,
    payload: boolean,
  } |
  {
    type: TestSolutionActionType.FinishTestSucceeded,
  } |
  {
    type: TestSolutionActionType.FinishTestExpired,
  } |
  {
    type: TestSolutionActionType.TimerSynced,
    payload: {
      remainingTime: number | null,
      hasTestTimeout: boolean,
      receivedAt: number,
    },
  } |
  {
    type: TestSolutionActionType.TimerElapsed,
  } |
  {
    type: TestSolutionActionType.ResumeAfterTimeout,
  };

export type TestSolutionDispatch = Dispatch<TestSolutionAction>;

type ContextValues = {
  questionType?: TestQuestionFrontType,
  currentQuestionIndex: number,
  answers: string[],
  userAnswer: unknown,
  isNextQuestionDisabled: boolean,
  isFileUpload: boolean,
  fileAnswer: string,
  timer: number | null,
  error: ValidationError,
  currentQuestion: TestQuestionData,
  isCommentRequiredAndEmpty: boolean,
  userComment: string,
  checkComponentCover: boolean,
  isTestInProgress: boolean,
  isTestExpired: boolean,
  isTestFinished: boolean,
};

type ContextActions = {
  startTest: () => void,
  continueTest: (questions: ContinueQuestion[]) => void,
  restartTest: () => void,
  prevQuestion: () => void,
  nextQuestionRequested: (questionCount: number) => Promise<void>,
  questionLoadedFromServer: (question?: TestQuestionData) => void,
  answerChanged: (value: SetStateAction<unknown>) => void,
  fileChanged: (value: SetStateAction<string>) => void,
  commentChanged: (value: SetStateAction<string>) => void,
  validationChanged: (value: SetStateAction<ValidationError>) => void,
  commentRequiredChanged: (value: boolean) => void,
  saveLoadingChanged: (value: boolean) => void,
  toggleCheckCover: (value: boolean) => void,
  setQuestionIndex: (index: number) => void,
  finishTestSucceeded: () => void,
  finishTestExpired: () => void,
  timerSynced: (payload: {
    remainingTime: number | null,
    hasTestTimeout: boolean,
    receivedAt: number,
  }) => void,
  resumeAfterTimeout: () => void,
  registerSaveAnswerHandler: (handler: SaveAnswerHandler | null) => void,
  onClickNextQuestion: (questionCount: number) => Promise<void>,
  onClickPrevQuestion: () => void,
  setUserAnswer: Dispatch<SetStateAction<unknown>>,
  setFileAnswer: Dispatch<SetStateAction<string>>,
  setError: Dispatch<SetStateAction<ValidationError>>,
  setIsCommentRequiredAndEmpty: Dispatch<SetStateAction<boolean>>,
  setUserComment: Dispatch<SetStateAction<string>>,
};

export const DEFAULT_CURRENT_QUESTION: TestQuestionData = {
  uid: "",
  question: "",
  questionType: "" as unknown as TestQuestionData["questionType"],
  questionTypeValue: "",
  status: "",
  answer: "",
  answers: "",
  currentAnswers: "",
  leftAnswers: "",
  rightAnswers: "",
  multiselect: "",
  shuffleButtons: "",
  userAnswer: "",
  fileAnswer: "",
  ball: null,
  maxball: null,
  uploadFile: "",
  questionImage: "",
  validation: "",
  validationInfo: "",
  surveyComment1: "",
  surveyComment2: "",
  stars: "",
  starsCount: "",
  requiredFeedback: "",
  additionalQuestionTitle: "",
  necessaryUserComment: "",
  userComment: "",
  triggerVariants: "",
  userCommentPlaceholder: "",
  descriptionLongread: "",
};

export const INITIAL_TEST_SOLUTION_STATE: TestSolutionState = {
  questionType: undefined,
  questionCurrentIndex: 0,
  userAnswer: "WAIT_USER_ANSWER_DATA",
  fileAnswer: "",
  isFileUpload: false,
  answers: [],
  currentQuestion: DEFAULT_CURRENT_QUESTION,
  isCommentRequiredAndEmpty: false,
  userComment: "",
  error: {
    hasError: false,
    message: "",
  },
  isSaveAnswerLoading: false,
  checkComponentCover: false,
  isTestInProgress: false,
  isTestExpired: false,
  isTestFinished: false,
  testDataReceivedAt: undefined,
  remainingTime: null,
  hasTestTimeout: false,
};

const INITIAL_CONTEXT_V: ContextValues = {
  questionType: undefined,
  currentQuestionIndex: 0,
  answers: [],
  userAnswer: null,
  isNextQuestionDisabled: true,
  isFileUpload: false,
  fileAnswer: "",
  timer: null,
  error: {
    hasError: false,
    message: "",
  },
  currentQuestion: DEFAULT_CURRENT_QUESTION,
  isCommentRequiredAndEmpty: false,
  userComment: "",
  checkComponentCover: false,
  isTestInProgress: false,
  isTestExpired: false,
  isTestFinished: false,
};

const INITIAL_CONTEXT_A: ContextActions = {
  startTest: () => null,
  continueTest: () => null,
  restartTest: () => null,
  prevQuestion: () => null,
  nextQuestionRequested: async () => undefined,
  questionLoadedFromServer: () => null,
  answerChanged: () => null,
  fileChanged: () => null,
  commentChanged: () => null,
  validationChanged: () => null,
  commentRequiredChanged: () => null,
  saveLoadingChanged: () => null,
  toggleCheckCover: () => null,
  setQuestionIndex: () => null,
  finishTestSucceeded: () => null,
  finishTestExpired: () => null,
  timerSynced: () => null,
  resumeAfterTimeout: () => null,
  registerSaveAnswerHandler: () => null,
  onClickNextQuestion: async () => undefined,
  onClickPrevQuestion: () => null,
  setUserAnswer: () => null,
  setFileAnswer: () => null,
  setError: () => null,
  setIsCommentRequiredAndEmpty: () => null,
  setUserComment: () => null,
};

export const TestSolutionValuesContext = createContext(INITIAL_CONTEXT_V);
export const TestSolutionActionsContext = createContext(INITIAL_CONTEXT_A);

export const useTestSolutionValuesContext = () =>
  useContext(TestSolutionValuesContext);
export const useTestSolutionActionsContext = () =>
  useContext(TestSolutionActionsContext);
